import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Typography } from 'antd';
import { Text } from '@/ui/typography';
import { Container, Flex } from '@/ui';
import { Button, Select } from '@/components/common/atoms';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { kycApi, adminKYCApi } from '@/api';
import { usePopups } from '@/hooks';

export const ModalDocument = ({
  isModalOpen,
  setIsModalOpen,
  visible,
  onClose,
  onSuccess,
  adminView = false,
  investorId
}) => {
  const { t } = useTranslation();
  const { showInfoToast } = usePopups();
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const { data: sections } = useQuery({
    queryKey: ['getDocumentsSections'],
    queryFn: adminView
      ? () => adminKYCApi.getDocumentsSections(investorId)
      : kycApi.getDocumentsSections
  });
  const { data: types, mutateAsync: fetchTypes } = useMutation({
    mutationFn: adminView ? adminKYCApi.getDocumentSectionTypes : kycApi.getDocumentSectionTypes
  });
  const { mutateAsync: requestDocument } = useMutation({
    mutationFn: adminView ? adminKYCApi.addDocumentRequest : kycApi.requestDocument
  });

  const docsSectionsList = useMemo(() => (sections ? sections.data : null), [sections]);
  const docsTypesList = useMemo(() => (types ? types.data : null), [types]);

  useEffect(() => {
    if (visible) {
      setSelectedType(null);
      setSelectedSection(null);
    }
  }, [visible]);

  useEffect(() => {
    if (selectedSection) {
      (async () => {
        try {
          await fetchTypes({
            sectionId: docsSectionsList.find((section) => section.id === selectedSection)?.id,
            investorId: investorId
          });
          setSelectedType(null);
        } catch (e) {
          console.error('FETCH_DOCS_TYPES', e);
          showInfoToast(e, true);
        }
      })();
    }
  }, [selectedSection]);

  const onRequestDoc = async () => {
    try {
      await requestDocument({
        docTypeId: docsTypesList.find((type) => type.id === selectedType)?.id,
        investorId
      });
      if (onSuccess) await onSuccess();
      showInfoToast('Successfully added');
      setSelectedSection(null);
      setSelectedType(null);
      setIsModalOpen(false);
      onClose();
    } catch (e) {
      console.error(e);
      showInfoToast(e, true);
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      closable
      footer={null}
      centered
    >
      <Container $paddingTop={20}>
        <Typography.Title level={3}>{t('Adding document')}</Typography.Title>

        <Container $marginTop={20} $marginBottom={8}>
          <Text $fontSize={14} $fontWeight={700}>
            {t('Section')}
          </Text>
        </Container>

        <Select
          allowClear
          placeholder={t('Select an item')}
          onChange={setSelectedSection}
          disabled={!docsSectionsList}
          value={selectedSection}
          options={
            docsSectionsList
              ? docsSectionsList.map((item) => {
                  return { value: item.id, label: String(item.name).toLocaleLowerCase() };
                })
              : []
          }
        />

        <Container $marginTop={18} $marginBottom={8}>
          <Text $fontSize={14} $fontWeight={700}>
            {t('Type')}
          </Text>
        </Container>

        <Select
          allowClear
          placeholder={t('Select an item')}
          placement='topLeft'
          value={selectedType}
          onChange={setSelectedType}
          disabled={!docsTypesList || docsTypesList.length === 0}
          options={
            docsTypesList
              ? docsTypesList.map((item) => {
                  return { value: item.id, label: String(item.name).toLocaleLowerCase() };
                })
              : []
          }
        />

        <Flex justify='flex-end'>
          <Container $marginTop={30}>
            <Button
              type='filled'
              text={t('Add document')}
              onClick={onRequestDoc}
              disabled={!selectedType}
            />
          </Container>
        </Flex>
      </Container>
    </Modal>
  );
};
